import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { SingleSelectComponent, MultiselectComponent, ModalComponent } from '../primeng';
import { AuthApiService, FileApiService, CatalogueApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { map } from 'rxjs/operators';
import { PdfViewerModule } from 'ng2-pdf-viewer';

function nonEmptyArrayValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  return Array.isArray(val) && val.length > 0 ? null : { required: true };
}

/** Resume/portfolio upload — must stay in sync with template label and `accept` attribute. */
const RESUME_PORTFOLIO_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.tiff',
  '.tif',
  '.svg',
  '.bmp',
];

const RESUME_PORTFOLIO_ACCEPT = RESUME_PORTFOLIO_ALLOWED_EXTENSIONS.join(',');

/** Preview in modal — PDF and images only (not DOC/DOCX). */
const RESUME_PREVIEWABLE_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.tiff',
  '.tif',
  '.svg',
  '.bmp',
];

const RESUME_PREVIEWABLE_EXTENSIONS = ['.pdf', ...RESUME_PREVIEWABLE_IMAGE_EXTENSIONS];

@Component({
  selector: 'app-request-credits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SingleSelectComponent,
    MultiselectComponent,
    ModalComponent,
    PdfViewerModule,
  ],
  templateUrl: './request-credits.component.html',
  styleUrl: './request-credits.component.scss',
})
export class RequestCreditsComponent implements OnInit, OnDestroy {
  /**
   * When used inside Admin "View" modal, we pass the API's `additionalInfo` here.
   * In that mode, we should NOT call getUserInfo(), and we should disable all fields.
   */
  @Input() initialData: any | null = null;
  @Input() readOnly: boolean = false;
  /** When true, user already has a pending credit request; form submit is disabled. */
  @Input() hasPendingRequest: boolean = false;

  /** Emitted when a credit request is successfully submitted so the parent can refresh pending state and show the warning/withdraw UI. */
  @Output() requestSubmitted = new EventEmitter<void>();

  /** Non-interactive: admin view modal, or the consumer's submission is awaiting approval. */
  get isCreditFormLocked(): boolean {
    return this.readOnly || this.hasPendingRequest;
  }

  requestCreditsForm!: FormGroup;
  private organizationDataFromApi: any = null;
  private userDataFromApi: any = null;
  private destroy$ = new Subject<void>();
  hasNoOrganisation: boolean = false; // Flag to track if user has no organisation
  /** When true, organisation applicant fields are shown (derived from API). */
  showOrganisationApplicantFieldsFromApi = false;
  /** Provider accounts are always org members. */
  isProviderUser = false;
  
  // Resume/portfolio preview (PDF + images — same pattern as Create Organisation docs)
  showPdfPreviewModal = false;
  currentPdfUrl: string | null = null;
  resumePreviewMode: 'pdf' | 'image' | null = null;
  resumePreviewTitle = 'Preview';
  resumePreviewLoading = false;
  pdfLoading = false;
  private resumeBlobUrlCache: string | null = null;
  uploadedResumeUrl: string | null = null;
  
  expandedSections: { [key: string]: boolean } = {
    'applicant-info': true,
    'project-overview': false,
    'technical-info': false,
    'team-credentials': false
  };
  
  // Character counters
  get projectDescriptionLength(): number {
    const value = this.requestCreditsForm?.get('projectDescription')?.value || '';
    return value.length;
  }
  
  get computeJustificationLength(): number {
    const value = this.requestCreditsForm?.get('computeJustification')?.value || '';
    return value.length;
  }
  
  get briefJustificationLength(): number {
    const value = this.requestCreditsForm?.get('briefJustification')?.value || '';
    return value.length;
  }

  get isEndUsersOther(): boolean {
    return this.requestCreditsForm?.get('endUsers')?.value === 'other';
  }

  get showPersonalApplicantFields(): boolean {
    return !this.showOrganisationApplicantFieldsFromApi;
  }

  get showOrganisationApplicantFields(): boolean {
    return this.showOrganisationApplicantFieldsFromApi;
  }

  private get usesOrganisationApplicantFields(): boolean {
    return this.showOrganisationApplicantFields;
  }

  hasOrganisationFieldValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return String(value).trim() !== '';
  }

  /** Type, location, and website are shown only when supplied by the API or saved request data. */
  shouldShowOrganisationDetailField(
    field: (typeof this.providerOptionalOrganisationFields)[number],
  ): boolean {
    const apiValue = this.organizationDataFromApi?.[field];
    if (this.hasOrganisationFieldValue(apiValue)) {
      return true;
    }
    const formValue = this.requestCreditsForm?.getRawValue()?.[field];
    return this.hasOrganisationFieldValue(formValue);
  }
  
  // Options for Request Credits form dropdowns
  projectSectorOptions = [
    { label: 'Agriculture & Farming', value: 'agriculture' },
    { label: 'Automotive/Mobility', value: 'automotive' },
    { label: 'Biotechnology', value: 'biotechnology' },
    { label: 'Climate & Sustainability', value: 'climate' },
    { label: 'Construction', value: 'construction' },
    { label: 'Consulting', value: 'consulting' },
    { label: 'E-commerce & Marketplaces', value: 'ecommerce' },
    { label: 'Education', value: 'education' },
    { label: 'Energy', value: 'energy' },
    { label: 'Finance', value: 'finance' },
    { label: 'Forestry', value: 'forestry' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Hospitality & Tourism', value: 'hospitality' },
    { label: 'Human Resources & Staffing', value: 'hr' },
    { label: 'Information Technology (IT)', value: 'it' },
    { label: 'Insurance', value: 'insurance' },
    { label: 'Investment & Asset Management', value: 'investment' },
    { label: 'Legal Services', value: 'legal' },
    { label: 'Logistics & Transportation', value: 'logistics' },
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Marketing & Advertising', value: 'marketing' },
    { label: 'Media & Entertainment', value: 'media' },
    { label: 'Mining & Metals', value: 'mining' },
    { label: 'MSME/Startup', value: 'msme' },
    { label: 'Non-Profit / NGO', value: 'nonprofit' },
    { label: 'Oil & Gas', value: 'oilgas' },
    { label: 'Public Enterprises', value: 'public-enterprises' },
    { label: 'Real Estate & Property Management', value: 'realestate' },
    { label: 'Retail & Consumer Goods', value: 'retail' },
    { label: 'Rural Development', value: 'rural' },
    { label: 'Sector Agnostic', value: 'agnostic' },
    { label: 'Semiconductor & Electronics', value: 'semiconductor' },
    { label: 'Shipping & Maritime', value: 'shipping' },
    { label: 'Sports & Recreation', value: 'sports' },
    { label: 'Telecommunications & Networking', value: 'telecom' },
    { label: 'Textiles & Apparel', value: 'textiles' },
    { label: 'Welfare', value: 'welfare' },
    { label: 'Other', value: 'other' }
  ];

  endUsersOptions = [
    { label: 'Public', value: 'public' },
    { label: 'Government', value: 'government' },
    { label: 'Enterprise', value: 'enterprise' },
    { label: 'Startups', value: 'startups' },
    { label: 'Academic/Research', value: 'academic' },
    { label: 'Non-profit/NGOs', value: 'nonprofit' },
    { label: 'Other', value: 'other' }
  ];

  projectStageOptions = [
    { label: 'Proof of Concept (PoC)', value: 'poc' },
    { label: 'Pilot', value: 'pilot' },
    { label: 'Minimum Viable Product (MVP)', value: 'mvp' },
    { label: 'Production', value: 'production' },
    { label: 'Scaling', value: 'scaling' }
  ];

  // Technical Information Options
  modelTypeOptions = [
    { label: 'Convolutional Neural Network (CNN)', value: 'cnn' },
    { label: 'Recurrent Neural Network (RNN)', value: 'rnn' },
    { label: 'Transformer', value: 'transformer' },
    { label: 'Generative Adversarial Network (GAN)', value: 'gan' },
    { label: 'Large Language Model (LLM)', value: 'llm' },
    { label: 'Computer Vision Model', value: 'cv' },
    { label: 'Natural Language Processing (NLP)', value: 'nlp' },
    { label: 'Reinforcement Learning', value: 'rl' },
    { label: 'Other', value: 'other' }
  ];

  trainingTypeOptions = [
    { label: 'Pre-trained', value: 'pretrained' },
    { label: 'New Training', value: 'new_training' }
  ];

  inputDataTypeOptions = [
    { label: 'Images', value: 'images' },
    { label: 'Text', value: 'text' },
    { label: 'Audio', value: 'audio' },
    { label: 'Video', value: 'video' },
    { label: 'Tabular Data', value: 'tabular' },
    { label: 'Time Series', value: 'timeseries' },
    { label: 'Multimodal', value: 'multimodal' },
    { label: 'Other', value: 'other' }
  ];

  dataVolumeOptions = [
    { label: '< 1 GB', value: 'lt_1gb' },
    { label: '1 GB - 10 GB', value: '1_10gb' },
    { label: '10 GB - 100 GB', value: '10_100gb' },
    { label: '100 GB - 1 TB', value: '100gb_1tb' },
    { label: '1 TB - 10 TB', value: '1_10tb' },
    { label: '> 10 TB', value: 'gt_10tb' }
  ];

  tgdexUsageOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ];

  // Team Credentials Options
  teamSizeOptions = [
    { label: '1-5 members', value: '1-5' },
    { label: '6-10 members', value: '6-10' },
    { label: '11-20 members', value: '11-20' },
    { label: '21-50 members', value: '21-50' },
    { label: '50+ members', value: '50+' }
  ];

  supportOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ];

  // Dataset dropdown state
  datasetOptions: { label: string; value: string }[] = [];
  datasetsLoading = false;
  datasetsLoadingMore = false;
  /** True while catalogue search runs from the dataset dropdown filter. */
  datasetsSearchLoading = false;
  private datasetPage = 1;
  private readonly datasetPageSize = 100;
  datasetHasMore = true;
  /** Debounced text used for catalogue `searchAllAssets(q=…)` alongside type filter. */
  private datasetSearchQuery = '';
  private readonly datasetSearch$ = new Subject<string>();
  private datasetSearchGeneration = 0;

  // File upload state
  readonly resumePortfolioAccept = RESUME_PORTFOLIO_ACCEPT;
  selectedResumeFile: File | null = null;
  resumeFileName: string = '';
  private readonly organisationFields = [
    'organisationName',
    'organisationType',
    'organisationLocation',
    'organisationWebsiteUrl',
    'jobTitle',
    'businessEmail'
  ] as const;

  /** Provider org members may omit type, location, and website when joining an organisation. */
  private readonly providerOptionalOrganisationFields = [
    'organisationType',
    'organisationLocation',
    'organisationWebsiteUrl',
  ] as const;

  constructor(
    private fb: FormBuilder,
    private authApiService: AuthApiService,
    private toasterService: ToasterService,
    private fileApiService: FileApiService,
    private catalogueApiService: CatalogueApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initRequestCreditsForm();
    this.setupEndUsersOtherValidator();

    this.datasetSearch$
      .pipe(
        debounceTime(300),
        map((raw) => (raw ?? '').trim()),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((query) => {
        this.datasetSearchQuery = query;
        void this.reloadDatasetOptionsFromSearch();
      });

    this.loadDatasets();

    // View mode: populate from input and disable everything
    if (this.readOnly) {
      this.applyInitialDataToForm(this.initialData);
      this.disableAllFields();
      return;
    }

    // Normal mode: apply initialData first if present (e.g. pending request from parent),
    // so form is prepopulated even when initialData was bound before ngOnInit (ngOnChanges runs before ngOnInit so form wasn't ready).
    if (this.initialData) {
      this.applyInitialDataToForm(this.initialData);
    }

    const skipProfilePrefill = this.hasPendingRequest && this.initialData != null;
    const finishInit = (): void => {
      this.syncPendingFormLock();
      setTimeout(() => this.checkAndSetResumeFileName(), 100);
    };

    if (skipProfilePrefill) {
      finishInit();
    } else {
      void this.loadUserData().finally(() => finishInit());
    }
  }
  
  /** File name used for display and preview type detection. */
  private getResumeDisplayFileName(): string {
    const fromName = (this.resumeFileName ?? '').trim();
    if (fromName) {
      return fromName;
    }
    const key = String(this.requestCreditsForm?.get('resumeFile')?.value ?? '').trim();
    return key ? (this.extractFileNameFromKey(key) ?? '') : '';
  }

  canPreviewResumeFile(): boolean {
    const name = this.getResumeDisplayFileName().toLowerCase();
    const hasFile =
      !!name ||
      !!String(this.requestCreditsForm?.get('resumeFile')?.value ?? '').trim();
    if (!hasFile) {
      return false;
    }
    return RESUME_PREVIEWABLE_EXTENSIONS.some((ext) => name.endsWith(ext));
  }

  isResumePreviewPdf(): boolean {
    return this.getResumePreviewMode() === 'pdf';
  }

  private getResumePreviewMode(): 'pdf' | 'image' | null {
    const name = this.getResumeDisplayFileName().toLowerCase();
    if (!name) {
      return null;
    }
    if (name.endsWith('.pdf')) {
      return 'pdf';
    }
    if (RESUME_PREVIEWABLE_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      return 'image';
    }
    return null;
  }

  /**
   * Check if resumeFile has a value and set resumeFileName accordingly
   */
  private checkAndSetResumeFileName(): void {
    if (!this.requestCreditsForm) return;
    
    const resumeFileValue = this.requestCreditsForm.get('resumeFile')?.value;
    if (resumeFileValue && resumeFileValue.trim() !== '' && !this.resumeFileName) {
      // Extract file name from the key
      const fileName = this.extractFileNameFromKey(resumeFileValue);
      if (fileName) {
        this.resumeFileName = fileName;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.requestCreditsForm) return;

    if (changes['initialData']) {
      const prev = changes['initialData'].previousValue;
      const curr = changes['initialData'].currentValue;

      if (this.readOnly) {
        this.applyInitialDataToForm(this.initialData);
        this.disableAllFields();
        return;
      }

      // Parent cleared initialData (e.g. after withdrawing pending request) -> reset form to empty and reload user data
      if (prev != null && (curr == null || curr === undefined)) {
        void this.clearForm();
        return;
      }

      if (curr != null) {
        this.applyInitialDataToForm(this.initialData);
      }
    }

    if (!this.readOnly && (changes['hasPendingRequest'] || changes['initialData'])) {
      this.syncPendingFormLock();
    }
  }

  ngOnDestroy(): void {
    this.closeResumePreview();
    this.revokeResumeBlobCache();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDatasets(): Promise<void> {
    this.datasetPage = 1;
    this.datasetHasMore = true;
    this.datasetOptions = [];
    this.datasetsLoading = true;
    try {
      const items = await this.fetchDatasetPage(this.datasetPage);
      this.datasetOptions = items;
      if (items.length < this.datasetPageSize) {
        this.datasetHasMore = false;
      }
    } catch (error) {
      console.error('Failed to load datasets:', error);
    } finally {
      this.datasetsLoading = false;
    }
  }

  onDatasetDropdownFilterChange(term: string): void {
    this.datasetSearch$.next(term ?? '');
  }

  /**
   * Re-run catalogue search for datasets (debounced filter text in `datasetSearchQuery`).
   * Ignores overlap with the initial page load guarded by {@link datasetsLoading}.
   */
  private async reloadDatasetOptionsFromSearch(): Promise<void> {
    if (this.datasetsLoading) {
      return;
    }
    const generation = ++this.datasetSearchGeneration;
    this.datasetsSearchLoading = true;
    this.datasetPage = 1;
    this.datasetHasMore = true;
    this.datasetOptions = [];
    this.cdr.markForCheck();
    try {
      const items = await this.fetchDatasetPage(1);
      if (generation !== this.datasetSearchGeneration) {
        return;
      }
      this.datasetOptions = items;
      this.datasetHasMore = items.length >= this.datasetPageSize;
    } catch (error) {
      if (generation === this.datasetSearchGeneration) {
        console.error('Failed to search datasets:', error);
        this.datasetOptions = [];
        this.datasetHasMore = false;
      }
    } finally {
      if (generation === this.datasetSearchGeneration) {
        this.datasetsSearchLoading = false;
      }
      this.cdr.markForCheck();
    }
  }

  async loadMoreDatasets(): Promise<void> {
    if (!this.datasetHasMore || this.datasetsLoadingMore || this.datasetsLoading || this.datasetsSearchLoading) return;
    this.datasetsLoadingMore = true;
    try {
      const nextPage = this.datasetPage + 1;
      const items = await this.fetchDatasetPage(nextPage);
      if (items.length === 0) {
        this.datasetHasMore = false;
      } else {
        this.datasetPage = nextPage;
        this.datasetOptions = [...this.datasetOptions, ...items];
        if (items.length < this.datasetPageSize) {
          this.datasetHasMore = false;
        }
      }
    } catch (error) {
      console.error('Failed to load more datasets:', error);
    } finally {
      this.datasetsLoadingMore = false;
    }
  }

  private async fetchDatasetPage(page: number): Promise<{ label: string; value: string }[]> {
    const response = await this.catalogueApiService.searchAllAssets(
      this.datasetSearchQuery,
      [],
      [],
      page,
      this.datasetPageSize,
      'itemCreatedAt:desc;',
      [{ searchType: 'term', field: 'type', values: ['adex:DataBank'] }]
    );

    let raw: any[] = [];
    if (response?.result && Array.isArray(response.result)) {
      raw = response.result;
    } else if (response?.data && Array.isArray(response.data)) {
      raw = response.data;
    } else if (Array.isArray(response)) {
      raw = response;
    }

    return raw
      .map((item: any) => ({
        label: item.name || item.label || item.id || '',
        value: item.name || item.id || ''
      }))
      .filter(opt => opt.label);
  }

  async loadUserData(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      this.userDataFromApi = userData;
      console.log('User data fetched:', userData);
      this.populateFormFromUserData(userData);
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      this.syncPendingFormLock();
    }
  }

  private applyInitialDataToForm(data: any | null): void {
    if (!data || !this.requestCreditsForm) {
      return;
    }

    const patch = this.normalizeAdditionalInfoForForm(data);
    this.requestCreditsForm.patchValue(patch, { emitEvent: false });
    this.applyResumeFileFromPatch(patch);

    const isMember = patch['isOrganizationMember'];
    if (isMember !== undefined) {
      this.syncApplicantFieldModeFromMembership(isMember === true, patch);
      this.applyApplicantInfoValidators(isMember === true);
    }
  }

  /** Restore applicant field mode when loading saved/history form data. */
  private syncApplicantFieldModeFromMembership(isMember: boolean, patch: Record<string, any>): void {
    const hasOrgFieldData = this.organisationFields.some((field) => {
      const value = patch[field];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
    const hasPersonalData =
      (patch['emailId'] != null && String(patch['emailId']).trim() !== '') ||
      (patch['location'] != null && String(patch['location']).trim() !== '');

    this.showOrganisationApplicantFieldsFromApi = isMember && hasOrgFieldData && !hasPersonalData;
    this.hasNoOrganisation = !isMember && !hasOrgFieldData;
  }

  /**
   * Map `additionalInfo` (or history payload) keys to form control names, including backend variants.
   */
  private normalizeAdditionalInfoForForm(data: any): Record<string, any> {
    const allowedKeys = Object.keys(this.requestCreditsForm.controls);
    const patch: Record<string, any> = {};

    allowedKeys.forEach((k) => {
      if (data[k] !== undefined && data[k] !== null) {
        patch[k] = data[k];
      }
    });

    const aliases: Record<string, string[]> = {
      organisationName: ['organizationName'],
      organisationType: ['organizationType', 'entityType'],
      organisationLocation: ['organizationLocation', 'location', 'address'],
      organisationWebsiteUrl: ['organizationWebsiteUrl', 'websiteLink', 'website', 'websiteUrl'],
    };
    for (const [formKey, altKeys] of Object.entries(aliases)) {
      if (patch[formKey] === undefined) {
        for (const altKey of altKeys) {
          if (data[altKey] !== undefined && data[altKey] !== null) {
            patch[formKey] = data[altKey];
            break;
          }
        }
      }
    }

    if (patch['isOrganizationMember'] === undefined) {
      if (data['is_organization_member'] !== undefined) {
        patch['isOrganizationMember'] = data['is_organization_member'];
      }
      if (data['isOrganizationMemberText'] !== undefined && patch['isOrganizationMember'] === undefined) {
        patch['isOrganizationMember'] = String(data['isOrganizationMemberText']).toLowerCase() === 'yes';
      }
    }

    if (patch['projectSector'] !== undefined && !Array.isArray(patch['projectSector'])) {
      patch['projectSector'] = patch['projectSector'] ? [patch['projectSector']] : [];
    }

    return patch;
  }

  private applyResumeFileFromPatch(patch: Record<string, any>): void {
    const resumeFileValue = patch['resumeFile'];
    if (resumeFileValue && String(resumeFileValue).trim() !== '') {
      this.uploadedResumeUrl = resumeFileValue;
      const fileName = this.extractFileNameFromKey(resumeFileValue);
      if (fileName) {
        this.resumeFileName = fileName;
      }
    }
  }

  private isProviderOptionalOrganisationField(
    field: (typeof this.organisationFields)[number],
  ): field is (typeof this.providerOptionalOrganisationFields)[number] {
    return (this.providerOptionalOrganisationFields as readonly string[]).includes(field);
  }

  /** Org fields required for section completion / validation (provider omits optional type, location, website). */
  private getRequiredOrganisationFields(): readonly string[] {
    return this.organisationFields.filter((field) => {
      if (!this.isProviderOptionalOrganisationField(field)) {
        return true;
      }
      if (this.isProviderUser) {
        return false;
      }
      return this.shouldShowOrganisationDetailField(field);
    });
  }

  /** Apply required validators for org vs personal fields based on visible field set. */
  private applyApplicantInfoValidators(isMember: boolean): void {
    const orgFields = [...this.organisationFields];
    const personalFields = ['emailId', 'location'];

    if (isMember) {
      personalFields.forEach((field) => {
        const control = this.requestCreditsForm.get(field);
        control?.clearValidators();
        control?.updateValueAndValidity({ emitEvent: false });
      });

      orgFields.forEach((field) => {
        const control = this.requestCreditsForm.get(field);
        if (this.isProviderOptionalOrganisationField(field)) {
          if (!this.shouldShowOrganisationDetailField(field) || this.isProviderUser) {
            control?.clearValidators();
          } else if (field === 'organisationWebsiteUrl') {
            control?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+/)]);
          } else {
            control?.setValidators([Validators.required]);
          }
        } else if (field === 'businessEmail') {
          control?.setValidators([Validators.required, Validators.email]);
        } else {
          control?.setValidators([Validators.required]);
        }
        control?.updateValueAndValidity({ emitEvent: false });
      });
      return;
    }

    orgFields.forEach((field) => {
      const control = this.requestCreditsForm.get(field);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

    const emailControl = this.requestCreditsForm.get('emailId');
    emailControl?.setValidators([Validators.required, Validators.email]);
    emailControl?.updateValueAndValidity({ emitEvent: false });

    const locationControl = this.requestCreditsForm.get('location');
    locationControl?.clearValidators();
    locationControl?.updateValueAndValidity({ emitEvent: false });
  }
  
  /**
   * Extract file name from a file key
   * Key format: "path/to/timestamp-uuid-uuid-uuid-filename.pdf" or "timestamp-uuid-uuid-uuid-filename.pdf"
   * The filename is the last segment that contains the file extension
   * Example: "1769053377844-29ca448b-048d-4721-ac2e-5ccd17685130-Forest-Stack Vocab.pdf"
   * Result: "Forest-Stack Vocab.pdf"
   */
  private extractFileNameFromKey(key: string): string | null {
    if (!key) return null;
    
    // If the key contains a slash, get the part after the last slash
    let lastPart = key;
    if (key.includes('/')) {
      const parts = key.split('/');
      lastPart = parts[parts.length - 1];
    }
    
    // The format is typically: "timestamp-uuid-uuid-uuid-filename.pdf"
    // Split by hyphens and find the segment(s) that contain the file extension
    if (lastPart.includes('-')) {
      const segments = lastPart.split('-');
      
      // Find the segment that contains the file extension (.pdf, .doc, .docx)
      let extensionIndex = -1;
      for (let i = segments.length - 1; i >= 0; i--) {
        if (segments[i].match(/\.(pdf|doc|docx)$/i)) {
          extensionIndex = i;
          break;
        }
      }
      
      if (extensionIndex >= 0) {
        // The filename starts from the segment with the extension
        // Check if previous segments are part of the filename (contain spaces or look like text)
        let startIndex = extensionIndex;
        for (let i = extensionIndex - 1; i >= 0; i--) {
          const seg = segments[i];
          // If segment contains spaces or is longer text (not a UUID), it's part of filename
          if (seg.includes(' ') || (seg.length > 15 && !seg.match(/^[0-9a-f-]+$/i))) {
            startIndex = i;
          } else {
            // This looks like a UUID or timestamp, stop here
            break;
          }
        }
        
        // Reconstruct the filename from the identified segments
        return segments.slice(startIndex).join('-');
      }
      
      // If no extension found, return the last part as-is
      return lastPart;
    }
    
    // If no hyphens, return as-is (might already be just the filename)
    return lastPart;
  }

  private disableAllFields(): void {
    if (!this.requestCreditsForm) return;
    this.requestCreditsForm.disable({ emitEvent: false });
  }

  /**
   * When the consumer already has a pending credit request, the whole form is disabled (read-only snapshot).
   * Admin `readOnly` mode is handled separately in ngOnInit/ngOnChanges.
   */
  private syncPendingFormLock(): void {
    if (!this.requestCreditsForm || this.readOnly) return;
    if (this.hasPendingRequest) {
      this.disableAllFields();
      return;
    }

    if (this.isProviderUser && this.showOrganisationApplicantFieldsFromApi) {
      this.applyProviderOrganisationFieldAdjustments();
    }
  }

  /**
   * Populate form fields from user API response
   */
  private populateFormFromUserData(userData: any): void {
    // Extract the result object if present, otherwise use userData directly
    const result = userData['result'] || userData;
    
    // Populate Applicant Name from API response
    const applicantName = result['name'] || 
                         result['userName'] || 
                         result['fullName'] || 
                         result['displayName'] || 
                         result['firstName'] || 
                         '';
    if (applicantName) {
      const applicantNameControl = this.requestCreditsForm.get('applicantName');
      if (applicantNameControl) {
        applicantNameControl.setValue(applicantName);
        applicantNameControl.disable(); // Keep it disabled
      }
    }

    // Always prefill personal email/location from users API for non-member path.
    const emailFromApi =
      result['email'] ||
      result['emailId'] ||
      result['businessEmail'] ||
      '';
    const locationFromApi =
      result['location'] ||
      result['address'] ||
      result['cityState'] ||
      '';
    this.requestCreditsForm.patchValue(
      {
        emailId: emailFromApi,
        location: locationFromApi,
      },
      { emitEvent: false }
    );

    // Disable any personal fields that were pre-filled from the API.
    if (emailFromApi) {
      this.requestCreditsForm.get('emailId')?.disable({ emitEvent: false });
    }
    if (locationFromApi) {
      this.requestCreditsForm.get('location')?.disable({ emitEvent: false });
    }

    // Extract organisation data — field set depends on organisation status
    const organisation = result['organisation'] || result['organization'] || result['org'] || null;
    const orgStatus = this.getNormalisedOrganisationStatus(organisation);

    if (organisation && this.isOrganisationApprovedStatus(orgStatus)) {
      // Granted: prefilled organisation fields
      this.organizationDataFromApi = this.buildOrganizationDataFromApi(organisation, result);
      this.showOrganisationApplicantFieldsFromApi = true;
      this.hasNoOrganisation = false;
      this.requestCreditsForm.patchValue({ isOrganizationMember: true }, { emitEvent: false });
      this.requestCreditsForm.patchValue(this.organizationDataFromApi, { emitEvent: false });
      this.disablePopulatedOrganisationFields();
      this.applyApplicantInfoValidators(true);
    } else {
      // Pending, org exists (non-granted), rejected, or no organisation: personal email/location
      this.applyPersonalApplicantFieldsMode(organisation, result);
    }

    this.detectAndApplyProviderOrgMembershipLock(userData);
  }

  private buildOrganizationDataFromApi(organisation: any, result: any): Record<string, string> {
    const organisationNameFromApi =
      organisation?.['name'] ||
      organisation?.['organisationName'] ||
      organisation?.['organizationName'] ||
      organisation?.['orgName'] ||
      result['organisationName'] ||
      result['organizationName'] ||
      result['orgName'] ||
      '';

    return {
      organisationName: organisationNameFromApi,
      organisationType:
        organisation?.['entityType'] ||
        organisation?.['orgSector'] ||
        organisation?.['type'] ||
        result['entityType'] ||
        result['organisationType'] ||
        result['organizationType'] ||
        '',
      organisationLocation:
        organisation?.['address'] ||
        organisation?.['location'] ||
        result['organisationLocation'] ||
        result['organizationLocation'] ||
        '',
      organisationWebsiteUrl:
        organisation?.['websiteLink'] ||
        organisation?.['website'] ||
        organisation?.['websiteUrl'] ||
        result['websiteLink'] ||
        result['organisationWebsiteUrl'] ||
        result['organizationWebsiteUrl'] ||
        '',
      jobTitle: organisation?.['jobTitle'] || result['jobTitle'] || '',
      businessEmail:
        organisation?.['managerEmail'] ||
        result['email'] ||
        organisation?.['email'] ||
        '',
    };
  }

  /** Pending / no organisation: show personal email and location for user entry. */
  private applyPersonalApplicantFieldsMode(organisation: any, result: any): void {
    this.showOrganisationApplicantFieldsFromApi = false;
    this.organizationDataFromApi = null;
    const hasOrgReference = !!(
      organisation ||
      result['organisationId'] ||
      result['organizationId']
    );
    this.hasNoOrganisation = !hasOrgReference;
    this.requestCreditsForm.patchValue({ isOrganizationMember: false }, { emitEvent: false });
    this.clearOrganisationFormFields();
    this.disableOrganisationFields();
    this.applyApplicantInfoValidators(false);

    if (!this.requestCreditsForm.get('emailId')?.value) {
      this.requestCreditsForm.get('emailId')?.enable({ emitEvent: false });
    }
    if (!this.requestCreditsForm.get('location')?.value) {
      this.requestCreditsForm.get('location')?.enable({ emitEvent: false });
    }
  }

  private getNormalisedOrganisationStatus(organisation: any): string | null {
    const rawStatus = organisation?.['status'];
    if (rawStatus == null || rawStatus === '') {
      return null;
    }
    return String(rawStatus).toLowerCase().trim();
  }

  private isOrganisationApprovedStatus(status: string | null): boolean {
    return status === 'granted';
  }

  private clearOrganisationFormFields(): void {
    const emptyValues = Object.fromEntries(
      this.organisationFields.map((field) => [field, '']),
    );
    this.requestCreditsForm.patchValue(emptyValues, { emitEvent: false });
  }

  private extractUserRoles(userData: any): string[] {
    const result = userData?.['result'] || userData || {};
    const roles = result?.roles ?? userData?.roles ?? [];
    return Array.isArray(roles) ? roles.map((role) => String(role).toLowerCase()) : [];
  }

  private detectAndApplyProviderOrgMembershipLock(userData: any): void {
    this.isProviderUser = this.extractUserRoles(userData).includes('provider');
    if (this.isProviderUser && this.showOrganisationApplicantFieldsFromApi) {
      this.applyProviderOrganisationFieldAdjustments();
    }
  }

  /** Provider users: type, location, and website are optional and prefilled from API when available. */
  private applyProviderOrganisationFieldAdjustments(): void {
    if (!this.requestCreditsForm || this.readOnly || this.isCreditFormLocked) {
      return;
    }
    this.applyProviderOptionalOrganisationFieldState();
  }

  /** Provider org members: optional type, location, website — prefilled and read-only when API supplies them. */
  private applyProviderOptionalOrganisationFieldState(): void {
    if (!this.isProviderUser || !this.requestCreditsForm) {
      return;
    }

    this.providerOptionalOrganisationFields.forEach((field) => {
      const control = this.requestCreditsForm.get(field);
      if (!control) {
        return;
      }

      const apiValue = this.organizationDataFromApi?.[field];
      const hasApiValue =
        apiValue !== null && apiValue !== undefined && String(apiValue).trim() !== '';

      control.clearValidators();
      if (hasApiValue) {
        control.setValue(apiValue, { emitEvent: false });
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private providerOrganisationFieldPayloadValue(
    _field: (typeof this.providerOptionalOrganisationFields)[number],
    value: unknown,
  ): string | null {
    const text = value != null ? String(value).trim() : '';
    return text || null;
  }

  /**
   * Disable organisation fields after pre-population
   */
  private disableOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (control) {
        control.disable();
      }
    });
  }

  private enableOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (control) {
        control.enable();
      }
    });
  }

  /** Disable only the org fields that have an API-supplied value; leave empty ones editable. */
  private disablePopulatedOrganisationFields(): void {
    this.organisationFields.forEach(field => {
      const control = this.requestCreditsForm.get(field);
      if (!control) return;

      const value = this.organizationDataFromApi?.[field];
      const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
      if (hasValue) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });

    if (this.isProviderUser) {
      this.applyProviderOptionalOrganisationFieldState();
    }
  }

  private hasCompleteOrganisationDetails(): boolean {
    if (!this.organizationDataFromApi) {
      return false;
    }
    return this.organisationFields.every((field) => {
      const value = this.organizationDataFromApi[field];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  initRequestCreditsForm(): void {
    this.requestCreditsForm = this.fb.group({
      // Applicant Information
      applicantName: [{ value: 'Org Mem20', disabled: true }, [Validators.required]],
      isOrganizationMember: [false],
      organisationName: [''],
      organisationType: [''],
      organisationLocation: [''],
      organisationWebsiteUrl: [''],
      jobTitle: [''],
      businessEmail: [''],
      emailId: [''],
      location: [''],
      
      // Project Overview
      projectTitle: ['', [Validators.required]],
      projectDescription: ['', [Validators.required, Validators.maxLength(250)]],
      projectSector: [[], [nonEmptyArrayValidator]],
      problemStatement: ['', [Validators.required]],
      tgdexDataset: ['', [Validators.required]],
      endUsers: ['', [Validators.required]],
      endUsersOther: [''],
      projectStage: ['', [Validators.required]],
      
      // Technical Information
      technicalInfo: [''],
      modelType: ['', [Validators.required]],
      trainingType: ['', [Validators.required]],
      inputDataType: ['', [Validators.required]],
      dataVolume: ['', [Validators.required]],
      trainingIterations: [''],
      estimatedBatchSize: [''],
      estimatedGpuHours: ['', [Validators.required]],
      briefJustification: ['', [Validators.required, Validators.maxLength(150)]],
      preferredTimeslots: [''],
      tgdexUsagePreviously: ['', [Validators.required]],
      
      // Team Credentials
      teamCredentials: [''],
      teamSize: ['', [Validators.required]],
      keyMembersRoles: [''],
      pastAiMlExperience: [''],
      projectsPublications: [''],
      resumeFile: [''], // Store file name or reference
      hasSupport: ['', [Validators.required]],
      latestValuation: [''],
      
      // Confirmations
      confirmAccuracy: [false, [Validators.requiredTrue]],
      understandGpuAllocation: [false, [Validators.requiredTrue]]
    });
  }

  private setupEndUsersOtherValidator(): void {
    const endUsersControl = this.requestCreditsForm.get('endUsers');
    const otherControl = this.requestCreditsForm.get('endUsersOther');
    if (!endUsersControl || !otherControl) return;

    endUsersControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === 'other') {
        otherControl.setValidators([Validators.required]);
      } else {
        otherControl.clearValidators();
        otherControl.setValue('');
      }
      otherControl.updateValueAndValidity();
    });
  }

  /**
   * Check if all required fields in Project Overview section are filled
   */
  private isProjectOverviewComplete(): boolean {
    const requiredFields = [
      'projectTitle',
      'projectDescription',
      'problemStatement',
      'projectSector',
      'endUsers',
      'tgdexDataset',
      'projectStage'
    ];
    
    return requiredFields.every(field => {
      const control = this.requestCreditsForm.get(field);
      if (!control || !control.valid || control.value === null) return false;
      if (Array.isArray(control.value)) return control.value.length > 0;
      return control.value !== '';
    });
  }

  toggleAccordionSection(sectionId: string): void {
    const wasExpanded = this.expandedSections[sectionId];
    
    // If opening a section (not closing), close all other sections first
    if (!wasExpanded) {
      // Close all other sections
      Object.keys(this.expandedSections).forEach(key => {
        if (key !== sectionId) {
          this.expandedSections[key] = false;
        }
      });
      
      // When opening technical-info (step 3), check if project-overview is complete
      if (sectionId === 'technical-info') {
        if (this.isProjectOverviewComplete()) {
          // All required fields filled - project-overview is already closed above
        } else {
          // Required fields not filled - keep project-overview open (but we just closed it, so don't open it)
          // This means if project-overview is incomplete, technical-info won't open
          // Instead, we should show a message or keep project-overview open
          // For now, we'll allow technical-info to open but user should complete project-overview first
        }
      }
      
      // When clicking on the second accordion (Project Overview), capture Applicant Information
      if (sectionId === 'project-overview') {
        this.captureApplicationInformation();
      }
    }
    
    // Toggle the clicked section
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  /**
   * Check if all required fields in Applicant Information section are filled and valid
   */
  private isApplicantInfoComplete(): boolean {
    const formValue = this.requestCreditsForm.getRawValue();
    
    // Check applicant name (required, but disabled so should always have value)
    if (!formValue.applicantName || formValue.applicantName.trim() === '') {
      return false;
    }
    
    const applicantNameControl = this.requestCreditsForm.get('applicantName');
    if (applicantNameControl?.invalid) {
      return false;
    }
    
    if (this.usesOrganisationApplicantFields) {
      for (const field of this.getRequiredOrganisationFields()) {
        const control = this.requestCreditsForm.get(field);
        if (!control || control.invalid || !control.value || control.value.trim() === '') {
          return false;
        }
      }
    } else {
      // Check personal fields
      const emailControl = this.requestCreditsForm.get('emailId');
      
      if (!emailControl || emailControl.invalid || !emailControl.value || emailControl.value.trim() === '') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Capture Applicant Information and log it to console
   */
  private captureApplicationInformation(): void {
    const formValue = this.requestCreditsForm.getRawValue(); // Use getRawValue() to include disabled fields
    
    const isOrganizationMember = formValue.isOrganizationMember || false;
    const switchValue = isOrganizationMember ? 'Yes' : 'No';
    
    const ApplicationInformation: any = {
      applicantName: formValue.applicantName || '',
      isOrganizationMember: isOrganizationMember,
      switchValue: switchValue
    };

    // If user uses organisation fields, include organization fields
    if (this.usesOrganisationApplicantFields) {
      ApplicationInformation.organisationName = formValue.organisationName || '';
      ApplicationInformation.organisationType = formValue.organisationType || '';
      ApplicationInformation.organisationLocation = formValue.organisationLocation || '';
      ApplicationInformation.organisationWebsiteUrl = formValue.organisationWebsiteUrl || '';
      ApplicationInformation.jobTitle = formValue.jobTitle || '';
      ApplicationInformation.businessEmail = formValue.businessEmail || '';
    } else {
      // If user is not an organization member, include personal fields
      ApplicationInformation.emailId = formValue.emailId || '';
      ApplicationInformation.location = formValue.location || '';
    }

    console.log('ApplicationInformation:', ApplicationInformation);
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections[sectionId] || false;
  }

  /**
   * Check if all required fields in Applicant Information section are complete
   */
  isApplicantInfoSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const formValue = this.requestCreditsForm.getRawValue(); // Use getRawValue to include disabled fields
    
    // Check applicant name (required, but may be disabled)
    if (!formValue.applicantName || String(formValue.applicantName).trim() === '') {
      return false;
    }
    
    if (this.usesOrganisationApplicantFields) {
      for (const field of this.getRequiredOrganisationFields()) {
        const control = this.requestCreditsForm.get(field);
        const value = formValue[field];
        if (!control || control.invalid || !value || String(value).trim() === '') {
          return false;
        }
      }
    } else {
      // Personal fields (non-member or granted org member)
      if (!formValue.emailId || String(formValue.emailId).trim() === '') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if all required fields in Project Overview section are complete
   */
  isProjectOverviewSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'projectTitle',
      'projectDescription',
      'projectSector',
      'problemStatement',
      'tgdexDataset',
      'endUsers',
      'projectStage'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (!control || control.invalid) return false;
      const val = control.value;
      if (Array.isArray(val)) {
        if (val.length === 0) return false;
      } else if (!val || String(val).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if all required fields in Technical Information section are complete
   */
  isTechnicalInfoSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'modelType',
      'trainingType',
      'inputDataType',
      'dataVolume',
      'estimatedGpuHours',
      'briefJustification',
      'tgdexUsagePreviously'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (control?.invalid || !control?.value || String(control.value).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if all required fields in Team Credentials section are complete
   */
  isTeamCredentialsSectionComplete(): boolean {
    if (!this.requestCreditsForm) return false;
    
    const requiredFields = [
      'teamSize',
      'hasSupport'
    ];

    for (const field of requiredFields) {
      const control = this.requestCreditsForm.get(field);
      if (control?.invalid || !control?.value || String(control.value).trim() === '') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if a specific section is complete
   */
  isSectionComplete(sectionId: string): boolean {
    switch (sectionId) {
      case 'applicant-info':
        return this.isApplicantInfoSectionComplete();
      case 'project-overview':
        return this.isProjectOverviewSectionComplete();
      case 'technical-info':
        return this.isTechnicalInfoSectionComplete();
      case 'team-credentials':
        return this.isTeamCredentialsSectionComplete();
      default:
        return false;
    }
  }

  /**
   * Get the completion state of a section: 'none', 'partial', 'complete'
   * In read-only (view) mode, submitted requests have all required fields filled, so show green for all sections.
   */
  getSectionCompletionState(sectionId: string): 'none' | 'partial' | 'complete' {
    if (this.isCreditFormLocked) return 'complete';
    if (!this.requestCreditsForm) return 'none';
    
    let totalFields = 0;
    let filledFields = 0;
    
    switch (sectionId) {
      case 'applicant-info':
        const formValue = this.requestCreditsForm.getRawValue();
        
        totalFields = 1; // applicantName
        if (formValue.applicantName && String(formValue.applicantName).trim() !== '') filledFields++;
        
        if (this.usesOrganisationApplicantFields) {
          const requiredOrgFields = this.getRequiredOrganisationFields();
          totalFields += requiredOrgFields.length;
          requiredOrgFields.forEach(field => {
            const value = formValue[field];
            if (value && String(value).trim() !== '') filledFields++;
          });
        } else {
          totalFields += 1; // emailId (location is optional)
          if (formValue.emailId && String(formValue.emailId).trim() !== '') filledFields++;
        }
        break;
        
      case 'project-overview':
        const projectFields = ['projectTitle', 'projectDescription', 'projectSector', 'problemStatement', 'tgdexDataset', 'endUsers', 'projectStage'];
        totalFields = projectFields.length;
        projectFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
        
      case 'technical-info':
        const techFields = ['modelType', 'trainingType', 'inputDataType', 'dataVolume', 'estimatedGpuHours', 'briefJustification', 'tgdexUsagePreviously'];
        totalFields = techFields.length;
        techFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
        
      case 'team-credentials':
        const teamFields = ['teamSize', 'hasSupport'];
        totalFields = teamFields.length;
        teamFields.forEach(field => {
          const control = this.requestCreditsForm.get(field);
          const value = control?.value;
          if (value && String(value).trim() !== '') filledFields++;
        });
        break;
    }
    
    if (filledFields === 0) return 'none';
    if (filledFields === totalFields) return 'complete';
    return 'partial';
  }

  /**
   * Get tooltip text for section completion
   */
  getSectionTooltip(sectionId: string): string {
    const state = this.getSectionCompletionState(sectionId);
    if (state === 'complete') {
      return 'All fields are filled';
    }
    return 'You need to fill the fields';
  }

  /**
   * Check if all sections have their required fields filled
   * Returns true only if all sections are complete (no red ticks)
   */
  areAllSectionsComplete(): boolean {
    const sections = ['applicant-info', 'project-overview', 'technical-info', 'team-credentials'];
    
    for (const sectionId of sections) {
      const state = this.getSectionCompletionState(sectionId);
      // If any section has empty required fields (state === 'none'), return false
      if (state === 'none') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Clear all form values after successful submission
   */
  async clearForm(): Promise<void> {
    if (!this.requestCreditsForm) return;
    
    // Clear file upload
    this.selectedResumeFile = null;
    this.resumeFileName = '';
    this.uploadedResumeUrl = null;
    
    // Clear file input element
    const fileInput = document.getElementById('resumeFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    this.closeResumePreview();
    this.revokeResumeBlobCache();
    
    // Reset accordion states - open only applicant-info
    this.expandedSections = {
      'applicant-info': true,
      'project-overview': false,
      'technical-info': false,
      'team-credentials': false
    };
    
    // Reset the form to initial state (values + pristine/touched flags)
    this.requestCreditsForm.reset();

    // `reset()` does not clear a disabled state on the group. Pending mode uses
    // `disable()` on the whole form — without `enable()`, the form stays frozen after withdraw.
    this.requestCreditsForm.enable({ emitEvent: false });

    // Default to personal fields until user API response determines the view
    this.showOrganisationApplicantFieldsFromApi = false;
    this.requestCreditsForm.patchValue({ isOrganizationMember: false });
    
    // Mark all form controls as untouched to clear validation errors
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
    
    // Reload user data to repopulate applicant name and organization data
    if (!this.readOnly) {
      await this.loadUserData();
    }
    
    // Reset dataset picker search/catalogue slice to defaults
    this.datasetSearchQuery = '';
    void this.loadDatasets();
    
    console.log('Form cleared after successful submission');
  }

  async onResumeFileSelected(event: Event): Promise<void> {
    if (this.isCreditFormLocked) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        console.error('File size exceeds 5MB limit');
        this.toasterService.error('File size exceeds 5MB limit');
        input.value = ''; // Clear the input
        return;
      }
      
      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!RESUME_PORTFOLIO_ALLOWED_EXTENSIONS.includes(fileExtension)) {
        console.error('Invalid file type for resume/portfolio upload.');
        this.toasterService.error(
          'Invalid file type. Allowed: PDF, DOC, DOCX, and images (JPG, PNG, GIF, WEBP, TIFF, SVG, BMP).'
        );
        input.value = ''; // Clear the input
        return;
      }
      
      this.selectedResumeFile = file;
      this.resumeFileName = file.name;
      this.cacheResumeBlobUrl(file);

      // Upload file to API
      try {
        this.pdfLoading = true;
        const uploadResponse = await this.fileApiService.assetUpload(file);
        console.log('File uploaded successfully:', uploadResponse);
        
        // Extract the file key and original name from the response
        // API response structure: { success: true, data: { key: "...", originalname: "...", ... } }
        // uploadResponse structure: { data: { success: true, data: { key: "...", originalname: "...", ... } }, status: number }
        const responseBody = uploadResponse?.data;
        const fileKey = responseBody?.data?.key; // Extract key from response.data.data.key
        const originalFileName = responseBody?.data?.originalname || file.name; // Extract original name or use file.name as fallback
        
        if (fileKey) {
          // Store the file key in the form (this is what we'll send to the API)
          this.requestCreditsForm.patchValue({
            resumeFile: fileKey
          });
          console.log('Resume file key stored:', fileKey);
          
          // Store the original file name for display
          this.resumeFileName = originalFileName;
          
          // Store the key for preview purposes (if needed)
          this.uploadedResumeUrl = fileKey;
          this.cacheResumeBlobUrl(file);
        } else {
          console.warn('File uploaded but no key found in response:', uploadResponse);
          // Fallback: store file name if key is not available
          this.requestCreditsForm.patchValue({
            resumeFile: file.name
          });
          this.resumeFileName = file.name;
        }
        
        // If it's a PDF, we can try to show preview (though preview might need a full URL)
        // For now, we'll skip auto-preview since we only have the key, not a full URL
        // The user can still click the PDF icon to preview if needed
        
        this.toasterService.success('File uploaded successfully');
      } catch (error) {
        console.error('Error uploading file:', error);
        this.toasterService.error('Failed to upload file. Please try again.');
        // Clear the file selection on error
        this.selectedResumeFile = null;
        this.resumeFileName = '';
        input.value = '';
      } finally {
        this.pdfLoading = false;
      }
    }
  }
  
  private cacheResumeBlobUrl(file: File): void {
    if (this.resumeBlobUrlCache) {
      URL.revokeObjectURL(this.resumeBlobUrlCache);
    }
    this.resumeBlobUrlCache = URL.createObjectURL(file);
  }

  private revokeResumeBlobCache(): void {
    if (this.resumeBlobUrlCache) {
      URL.revokeObjectURL(this.resumeBlobUrlCache);
      this.resumeBlobUrlCache = null;
    }
  }

  private clearResumePreviewUrl(): void {
    if (this.currentPdfUrl?.startsWith('blob:') && this.currentPdfUrl !== this.resumeBlobUrlCache) {
      URL.revokeObjectURL(this.currentPdfUrl);
    }
    this.currentPdfUrl = null;
  }

  /**
   * Trigger change detection so the PDF viewer renders (avoids empty modal until user clicks).
   */
  private triggerPreviewRender(): void {
    this.cdr.detectChanges();
    setTimeout(() => this.cdr.detectChanges(), 150);
  }

  /**
   * Open resume/portfolio preview (PDF or image only).
   */
  async openResumePreview(): Promise<void> {
    const mode = this.getResumePreviewMode();
    if (!mode) {
      this.toasterService.error('Preview is only available for PDF and image files.');
      return;
    }

    this.resumePreviewMode = mode;
    this.resumePreviewTitle =
      mode === 'pdf' ? 'Preview PDF' : 'Preview Image';

    if (this.resumeBlobUrlCache) {
      this.currentPdfUrl = this.resumeBlobUrlCache;
      this.showPdfPreviewModal = true;
      this.triggerPreviewRender();
      return;
    }

    const localFile = this.selectedResumeFile;
    if (localFile) {
      this.cacheResumeBlobUrl(localFile);
      this.currentPdfUrl = this.resumeBlobUrlCache;
      this.showPdfPreviewModal = true;
      this.triggerPreviewRender();
      return;
    }

    const resumeFileKey = String(this.requestCreditsForm?.get('resumeFile')?.value ?? '').trim();
    if (!resumeFileKey) {
      this.toasterService.error('No file available for preview.');
      return;
    }

    this.resumePreviewLoading = true;
    this.currentPdfUrl = null;
    this.showPdfPreviewModal = true;

    try {
      const downloadResponse = await this.fileApiService.downloadAssets({
        key: resumeFileKey,
        expiresIn: 0,
      });

      const downloadUrl =
        downloadResponse?.data?.url ||
        downloadResponse?.url ||
        downloadResponse?.result?.url ||
        downloadResponse?.downloadUrl;

      if (!downloadUrl) {
        throw new Error('No download URL found in response');
      }

      const blob = await this.fetchFileAsBlob(downloadUrl);
      const blobUrl = URL.createObjectURL(blob);
      this.currentPdfUrl = blobUrl;
      this.triggerPreviewRender();
    } catch (error) {
      console.error('Error loading resume preview:', error);
      this.toasterService.error('Failed to load file preview. Please try again.');
      this.showPdfPreviewModal = false;
      this.resumePreviewMode = null;
    } finally {
      this.resumePreviewLoading = false;
      this.cdr.detectChanges();
    }
  }

  private async fetchFileAsBlob(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }
    return response.blob();
  }

  closeResumePreview(): void {
    this.showPdfPreviewModal = false;
    this.clearResumePreviewUrl();
    this.resumePreviewMode = null;
    this.resumePreviewLoading = false;
  }

  /**
   * Select support option (Yes/No)
   */
  selectSupportOption(value: string): void {
    if (this.isCreditFormLocked) return;
    this.requestCreditsForm.patchValue({
      hasSupport: value
    });
    this.requestCreditsForm.get('hasSupport')?.markAsTouched();
  }

  /**
   * Select training type option
   */
  selectTrainingType(value: string): void {
    if (this.isCreditFormLocked) return;
    this.requestCreditsForm.patchValue({
      trainingType: value
    });
    this.requestCreditsForm.get('trainingType')?.markAsTouched();
  }

  /**
   */
  selectTgdexUsage(value: string): void {
    if (this.isCreditFormLocked) return;
    this.requestCreditsForm.patchValue({
      tgdexUsagePreviously: value
    });
    this.requestCreditsForm.get('tgdexUsagePreviously')?.markAsTouched();
  }

  removeResumeFile(): void {
    if (this.isCreditFormLocked) return;
    this.selectedResumeFile = null;
    this.resumeFileName = '';
    this.uploadedResumeUrl = null;
    this.requestCreditsForm.patchValue({
      resumeFile: ''
    });

    this.clearResumePreviewUrl();
    this.revokeResumeBlobCache();
    this.resumePreviewMode = null;
    
    // Clear the file input
    const fileInput = document.getElementById('resumeFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  async requestCredits(): Promise<void> {
    if (this.isCreditFormLocked) return;

    console.log('Submit button clicked - requestCredits() called');
    
    // Mark all form controls as touched to show validation errors
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      control?.markAsTouched();
    });
    
    // Check if form is valid
    if (!this.requestCreditsForm.valid) {
      console.warn('Form is invalid, cannot submit');
      console.log('Form valid:', this.requestCreditsForm.valid);
      console.log('Form errors:', this.requestCreditsForm.errors);
      
      // Log all invalid fields
      const invalidFields: string[] = [];
      Object.keys(this.requestCreditsForm.controls).forEach(key => {
        const control = this.requestCreditsForm.get(key);
        if (control?.invalid) {
          invalidFields.push(key);
          console.log(`Field ${key} is invalid:`, control.errors);
        }
      });
      console.log('Invalid fields:', invalidFields);
      
      // Show error message
      this.toasterService.error('Please fill all required fields before submitting');
      return;
    }
    
    console.log('Form valid:', this.requestCreditsForm.valid);
    console.log('Form errors:', this.requestCreditsForm.errors);
    
    // Get all form values as an object (regardless of validity)
    const formData = this.requestCreditsForm.getRawValue();
    console.log('Raw form data:', formData);
    
    // Get switch value explicitly
    const switchValue = formData.isOrganizationMember;
    const switchValueText = switchValue ? 'Yes' : 'No';
    console.log('Switch value (isOrganizationMember):', switchValue);
    console.log('Switch value (text):', switchValueText);
    
    // Create a comprehensive object with all field values
    const allFormValues: { [key: string]: any } = {
      // Applicant Information
      applicantName: formData.applicantName || null,
      isOrganizationMember: switchValue || false,
      isOrganizationMemberText: switchValueText, // Add text representation
      
      // Organization fields (member, not granted) vs personal fields (non-member or granted)
      ...(this.usesOrganisationApplicantFields ? {
        organisationName: formData.organisationName || null,
        organisationType: this.providerOrganisationFieldPayloadValue(
          'organisationType',
          formData.organisationType,
        ),
        organisationLocation: this.providerOrganisationFieldPayloadValue(
          'organisationLocation',
          formData.organisationLocation,
        ),
        organisationWebsiteUrl: this.providerOrganisationFieldPayloadValue(
          'organisationWebsiteUrl',
          formData.organisationWebsiteUrl,
        ),
        jobTitle: formData.jobTitle || null,
        businessEmail: formData.businessEmail || null
      } : {
        emailId: formData.emailId || null,
        location: formData.location || null
      }),
      
      // Project Overview
      projectTitle: formData.projectTitle || null,
      projectDescription: formData.projectDescription || null,
      problemStatement: formData.problemStatement || null,
      projectSector: (Array.isArray(formData.projectSector) ? formData.projectSector : formData.projectSector ? [formData.projectSector] : null),
      endUsers: formData.endUsers || null,
      endUsersOther: formData.endUsers === 'other' ? (formData.endUsersOther || null) : null,
      tgdexDataset: formData.tgdexDataset || null,
      projectStage: formData.projectStage || null,
      
      // Technical Information
      technicalInfo: formData.technicalInfo || null,
      modelType: formData.modelType || null,
      trainingType: formData.trainingType || null,
      inputDataType: formData.inputDataType || null,
      dataVolume: formData.dataVolume || null,
      trainingIterations: formData.trainingIterations !== '' && formData.trainingIterations != null
        ? Number(formData.trainingIterations)
        : null,
      estimatedBatchSize: formData.estimatedBatchSize !== '' && formData.estimatedBatchSize != null
        ? Number(formData.estimatedBatchSize)
        : null,
      estimatedGpuHours: formData.estimatedGpuHours !== '' && formData.estimatedGpuHours != null
        ? Number(formData.estimatedGpuHours)
        : null,
      briefJustification: formData.briefJustification || null,
      preferredTimeslots: formData.preferredTimeslots || null,
      tgdexUsagePreviously: formData.tgdexUsagePreviously || null,
      
      // Team Credentials
      teamCredentials: formData.teamCredentials || null,
      teamSize: formData.teamSize || null,
      keyMembersRoles: formData.keyMembersRoles || null,
      pastAiMlExperience: formData.pastAiMlExperience || null,
      projectsPublications: formData.projectsPublications || null,
      resumeFile: formData.resumeFile || null, // Use the stored file key from upload response (data.key)
      hasSupport: formData.hasSupport || null,
      latestValuation: formData.latestValuation || null,
      
      // Confirmations
      confirmAccuracy: formData.confirmAccuracy || false,
      understandGpuAllocation: formData.understandGpuAllocation || false
    };
    
    console.log('All form values as object:', allFormValues);
    console.log('Form values (key-value pairs):', JSON.stringify(allFormValues, null, 2));
    
    // Log detailed validation status
    const invalidFields: string[] = [];
    Object.keys(this.requestCreditsForm.controls).forEach(key => {
      const control = this.requestCreditsForm.get(key);
      if (control?.invalid) {
        invalidFields.push(key);
        control.markAsTouched();
        console.log(`Field ${key} is invalid:`, control.errors);
      }
    });
    
    console.log('Invalid fields:', invalidFields);
    console.log('Form valid status:', this.requestCreditsForm.valid);
    console.log('Form status:', this.requestCreditsForm.status);
    
    // Call API regardless of validation (or you can keep the validation check)
    // For now, let's call it even if invalid to see if it works, but log a warning
    if (!this.requestCreditsForm.valid) {
      console.warn('Form has validation errors, but proceeding with API call anyway');
      console.warn('Invalid fields:', invalidFields);
    }
    
    try {
      // Wrap all form values under additionalInfo as required by API
      const requestPayload = {
        additionalInfo: allFormValues,
      };

      console.log('Calling credit request API with payload:', requestPayload);
      const response = await this.authApiService.getCreditRequestPost(requestPayload);
      console.log('Credit request API response received:', response);
      
      // If we reach here, the request was successful (200 OK)
      // Show green success toaster
      this.toasterService.success("You're request has been sent successfully");
      console.log('Request sent successfully - 200 OK');
      
      // Notify parent so it can refresh pending state and show the pending warning div with withdraw button
      this.requestSubmitted.emit();

      // Keep showing the submitted snapshot; parent refreshes pending + initialData and we lock via hasPendingRequest.
    } catch (error: any) {
      console.error('Error calling computeRequest API:', error);
      
      // Check if error is a 409 Conflict
      // Error structure can vary: error.status, error.error.status, or error.response?.status
      const statusCode = error?.status || error?.error?.status || error?.response?.status;
      
      if (statusCode === 409) {
        // Extract detail message from error response
        // Try multiple possible locations for the detail message
        const detailMessage = 
          error?.error?.detail || 
          error?.detail || 
          error?.response?.data?.detail ||
          error?.response?.detail ||
          error?.message || 
          'Entry already exists';
        
        // Show red error toaster with detail message
        this.toasterService.error(detailMessage);
        console.error('409 Conflict error - detail:', detailMessage);
        console.error('Full error object:', error);
      } else {
        // For other errors, let NetworkService handle it (toaster will be shown automatically)
        console.error('Other error occurred:', error);
      }
    }
  }

  /**
   * Fill form from credit request history data (called when user clicks "Fill" in history modal).
   * Populates all fields from the additionalInfo object.
   */
  fillFormFromHistory(additionalInfo: any): void {
    if (!additionalInfo || !this.requestCreditsForm) {
      return;
    }
    if (this.isCreditFormLocked) {
      return;
    }

    const formData = this.normalizeAdditionalInfoForForm(additionalInfo);

    // User must re-confirm before resubmitting a historical request
    delete formData['confirmAccuracy'];
    delete formData['understandGpuAllocation'];

    const isMember = formData['isOrganizationMember'] ?? this.requestCreditsForm.get('isOrganizationMember')?.value;
    this.syncApplicantFieldModeFromMembership(isMember === true, formData);
    if (isMember === true) {
      // Enable first so patchValue can update fields that were disabled by profile pre-fill
      this.enableOrganisationFields();
    }

    this.requestCreditsForm.patchValue(formData, { emitEvent: false });
    this.applyResumeFileFromPatch(formData);

    if (isMember !== null && isMember !== undefined) {
      this.applyApplicantInfoValidators(isMember);
    }

    Object.keys(formData).forEach((key) => {
      this.requestCreditsForm.get(key)?.markAsTouched();
      this.requestCreditsForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    });

    this.expandedSections = {
      'applicant-info': true,
      'project-overview': true,
      'technical-info': true,
      'team-credentials': true
    };

    if (this.isProviderUser && this.showOrganisationApplicantFieldsFromApi) {
      this.applyProviderOrganisationFieldAdjustments();
    }

    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }
}
